/**
 * Training Service - Funkcje do zarządzania szkoleniami medycznymi
 * wojskaspecjalne.pl
 */

import { supabase } from './supabase';
import type { Instructor } from './instructorService';

// ========================================
// TYPES
// ========================================

export type TrainingStatus = 'draft' | 'published' | 'cancelled' | 'completed';
export type TrainingDifficulty = 'podstawowy' | 'średni' | 'zaawansowany';
export type RegistrationStatus = 'registered' | 'confirmed' | 'waitlisted' | 'cancelled' | 'attended' | 'no-show';
export type PaymentStatus = 'pending' | 'paid' | 'refunded' | 'free';

export interface Training {
  id: string;
  title: string;
  description: string | null;
  slug: string | null;
  instructor_id: string | null;
  category_id: string | null;
  type_id: string | null;
  shooting_range_id: string | null;
  difficulty_level: TrainingDifficulty;
  price_amount: number;
  price_currency: string;
  price_description: string | null;
  start_date: string;
  end_date: string;
  timezone: string;
  location_name: string;
  location_address: string | null;
  location_city: string | null;
  location_country: string;
  location_coordinates: { lat: number; lng: number } | null;
  max_participants: number;
  current_participants: number;
  duration_minutes: number | null;
  equipment_required: string | null;
  min_age: number | null;
  registration_open_at: string | null;
  registration_close_at: string | null;
  cancellation_deadline: string | null;
  is_waitlist_enabled: boolean;
  cover_image_url: string | null;
  status: TrainingStatus;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  published_at: string | null;
  // Populated fields
  instructor?: Instructor;
}

export interface TrainingPrerequisite {
  id: string;
  training_id: string;
  prerequisite_training_id: string;
  created_at: string;
  // Populated fields
  prerequisite_training?: Training;
}

export interface TrainingRegistration {
  id: string;
  training_id: string;
  user_id: string;
  registration_status: RegistrationStatus;
  registration_date: string;
  payment_status: PaymentStatus;
  payment_amount: number | null;
  payment_date: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  // Populated fields
  training?: Training;
}

export interface TrainingWithDetails extends Training {
  instructor?: Instructor;
  prerequisites?: Training[];
  registrations?: TrainingRegistration[];
  is_user_registered?: boolean;
  is_full?: boolean;
}

export interface CreateTrainingData {
  title: string;
  description?: string;
  slug?: string | null;
  instructor_id?: string | null;
  category_id?: string | null;
  type_id?: string | null;
  shooting_range_id?: string | null;
  difficulty_level?: TrainingDifficulty;
  price_amount?: number;
  price_currency?: string;
  price_description?: string | null;
  start_date: string;
  end_date: string;
  timezone?: string;
  location_name: string;
  location_address?: string | null;
  location_city?: string | null;
  location_country?: string;
  location_coordinates?: { lat: number; lng: number } | null;
  max_participants?: number;
  cover_image_url?: string | null;
  status?: TrainingStatus;
  is_waitlist_enabled?: boolean;
  prerequisite_training_ids?: string[];
}

export interface UpdateTrainingData extends Partial<CreateTrainingData> {
  id: string;
}

// ========================================
// TRAININGS CRUD
// ========================================

/**
 * Pobiera wszystkie szkolenia
 */
export async function getAllTrainings(options?: {
  includeUnpublished?: boolean;
  search?: string;
  difficulty?: TrainingDifficulty;
  instructorId?: string;
}): Promise<{ success: boolean; data?: Training[]; error?: string }> {
  try {
    let query = supabase
      .from('trainings')
      .select(`
        *,
        instructors (*)
      `)
      .order('start_date', { ascending: true, nullsFirst: false });

    // Filter by status
    if (!options?.includeUnpublished) {
      query = query.eq('status', 'published');
    }

    // Filter by difficulty
    if (options?.difficulty) {
      query = query.eq('difficulty_level', options.difficulty);
    }

    // Filter by instructor
    if (options?.instructorId) {
      query = query.eq('instructor_id', options.instructorId);
    }

    // Search
    if (options?.search) {
      query = query.or(
        `title.ilike.%${options.search}%,description.ilike.%${options.search}%`
      );
    }

    const { data, error } = await query;

    if (error) throw error;

    // Transform data
    const trainings: Training[] = (data || []).map((training: any) => ({
      ...training,
      instructor: training.instructors || undefined,
    }));

    return { success: true, data: trainings };
  } catch (error: any) {
    console.error('Error fetching trainings:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Pobiera szkolenie po ID
 */
export async function getTrainingById(
  trainingId: string
): Promise<{ success: boolean; data?: TrainingWithDetails; error?: string }> {
  try {
    const { data: training, error: trainingError } = await supabase
      .from('trainings')
      .select(`
        *,
        instructors (*)
      `)
      .eq('id', trainingId)
      .single();

    if (trainingError) throw trainingError;
    if (!training) throw new Error('Training not found');

    // Get prerequisites
    const { data: prerequisites } = await supabase
      .from('training_prerequisites')
      .select(`
        *,
        prerequisite_training:trainings!training_prerequisites_prerequisite_training_id_fkey (*)
      `)
      .eq('training_id', trainingId);

    // Get registrations count
    const { count: registrationsCount } = await supabase
      .from('training_registrations')
      .select('*', { count: 'exact', head: true })
      .eq('training_id', trainingId)
      .in('registration_status', ['registered', 'confirmed']);

    const result: TrainingWithDetails = {
      ...training,
      instructor: training.instructors || undefined,
      prerequisites: (prerequisites || []).map((p: any) => p.prerequisite_training).filter(Boolean),
      current_participants: registrationsCount || 0,
      is_full: (registrationsCount || 0) >= training.max_participants,
    };

    return { success: true, data: result };
  } catch (error: any) {
    console.error('Error fetching training:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Pobiera szkolenie po slug
 */
export async function getTrainingBySlug(
  slug: string
): Promise<{ success: boolean; data?: TrainingWithDetails; error?: string }> {
  try {
    const { data: training, error: trainingError } = await supabase
      .from('trainings')
      .select(`
        *,
        instructors (*)
      `)
      .eq('slug', slug)
      .eq('status', 'published')
      .single();

    if (trainingError) throw trainingError;
    if (!training) throw new Error('Training not found');

    // Get prerequisites
    const { data: prerequisites } = await supabase
      .from('training_prerequisites')
      .select(`
        *,
        prerequisite_training:trainings!training_prerequisites_prerequisite_training_id_fkey (*)
      `)
      .eq('training_id', training.id);

    // Get registrations count
    const { count: registrationsCount } = await supabase
      .from('training_registrations')
      .select('*', { count: 'exact', head: true })
      .eq('training_id', training.id)
      .in('registration_status', ['registered', 'confirmed']);

    const result: TrainingWithDetails = {
      ...training,
      instructor: training.instructors || undefined,
      prerequisites: (prerequisites || []).map((p: any) => p.prerequisite_training).filter(Boolean),
      current_participants: registrationsCount || 0,
      is_full: (registrationsCount || 0) >= training.max_participants,
    };

    return { success: true, data: result };
  } catch (error: any) {
    console.error('Error fetching training by slug:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Tworzy nowe szkolenie
 */
export async function createTraining(
  trainingData: CreateTrainingData,
  userId: string
): Promise<{ success: boolean; data?: Training; error?: string }> {
  try {
    const { prerequisite_training_ids, ...trainingFields } = trainingData;

    if (!trainingData.title?.trim()) {
      throw new Error('Training title is required');
    }
    if (!trainingData.location_name?.trim()) {
      throw new Error('Training location is required');
    }
    if (!trainingData.start_date) {
      throw new Error('Training start_date is required');
    }
    if (!trainingData.end_date) {
      throw new Error('Training end_date is required');
    }

    // Insert training
    const { data: training, error: trainingError } = await supabase
      .from('trainings')
      .insert({
        ...trainingFields,
        created_by: userId,
        timezone: trainingData.timezone || 'Europe/Warsaw',
        price_amount: typeof trainingData.price_amount === 'number' ? trainingData.price_amount : 0,
        price_currency: trainingData.price_currency || 'PLN',
        location_country: trainingData.location_country || 'Poland',
        difficulty_level: trainingData.difficulty_level || 'podstawowy',
        max_participants: trainingData.max_participants || 20,
        status: trainingData.status || 'draft',
        is_waitlist_enabled: trainingData.is_waitlist_enabled ?? true,
      })
      .select()
      .single();

    if (trainingError) throw trainingError;
    if (!training) throw new Error('Failed to create training');

    // Add prerequisites if provided
    if (prerequisite_training_ids && prerequisite_training_ids.length > 0) {
      const prerequisites = prerequisite_training_ids.map(prereqId => ({
        training_id: training.id,
        prerequisite_training_id: prereqId,
      }));

      const { error: prereqError } = await supabase
        .from('training_prerequisites')
        .insert(prerequisites);

      if (prereqError) {
        console.error('Error creating prerequisites:', prereqError);
        // Don't fail the whole operation, just log the error
      }
    }

    return { success: true, data: training };
  } catch (error: any) {
    console.error('Error creating training:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Aktualizuje szkolenie
 */
export async function updateTraining(
  trainingData: UpdateTrainingData
): Promise<{ success: boolean; data?: Training; error?: string }> {
  try {
    const { id, prerequisite_training_ids, ...updateFields } = trainingData;

    // Update training
    const { data: training, error: trainingError } = await supabase
      .from('trainings')
      .update(updateFields)
      .eq('id', id)
      .select()
      .single();

    if (trainingError) throw trainingError;
    if (!training) throw new Error('Training not found');

    // Update prerequisites if provided
    if (prerequisite_training_ids !== undefined) {
      // Delete existing prerequisites
      await supabase
        .from('training_prerequisites')
        .delete()
        .eq('training_id', id);

      // Add new prerequisites
      if (prerequisite_training_ids.length > 0) {
        const prerequisites = prerequisite_training_ids.map(prereqId => ({
          training_id: id,
          prerequisite_training_id: prereqId,
        }));

        const { error: prereqError } = await supabase
          .from('training_prerequisites')
          .insert(prerequisites);

        if (prereqError) {
          console.error('Error updating prerequisites:', prereqError);
        }
      }
    }

    return { success: true, data: training };
  } catch (error: any) {
    console.error('Error updating training:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Usuwa szkolenie
 */
export async function deleteTraining(
  trainingId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('trainings')
      .delete()
      .eq('id', trainingId);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error('Error deleting training:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Publikuje szkolenie
 */
export async function publishTraining(
  trainingId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('trainings')
      .update({
        status: 'published',
        published_at: new Date().toISOString(),
      })
      .eq('id', trainingId);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error('Error publishing training:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Anuluje szkolenie
 */
export async function cancelTraining(
  trainingId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('trainings')
      .update({ status: 'cancelled' })
      .eq('id', trainingId);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error('Error cancelling training:', error);
    return { success: false, error: error.message };
  }
}

// ========================================
// REGISTRATIONS
// ========================================

/**
 * Rejestruje użytkownika na szkolenie
 */
export async function registerForTraining(
  trainingId: string,
  userId: string
): Promise<{ success: boolean; data?: TrainingRegistration; error?: string }> {
  try {
    // Inserts are blocked by RLS to enforce atomic capacity logic.
    // Use RPC that creates a seat-hold or waitlist entry.
    const { data: registration, error: regError } = await supabase
      .rpc('request_training_booking', { p_training_id: trainingId })
      .single();

    if (regError || !registration) {
      throw regError || new Error('Booking failed');
    }

    // NOTE: for paid trainings this will create a "registered + pending" hold.
    // Frontend should continue to Stripe Checkout to confirm payment.
    return { success: true, data: registration as any };
  } catch (error: any) {
    console.error('Error registering for training:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Sprawdza czy użytkownik jest zarejestrowany na szkolenie
 */
export async function isUserRegisteredForTraining(
  trainingId: string,
  userId: string
): Promise<{ success: boolean; isRegistered: boolean; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('training_registrations')
      .select('id')
      .eq('training_id', trainingId)
      .eq('user_id', userId)
      .in('registration_status', ['registered', 'confirmed'])
      .single();

    if (error && error.code !== 'PGRST116') {
      // PGRST116 = no rows returned, which is fine
      throw error;
    }

    return { success: true, isRegistered: !!data };
  } catch (error: any) {
    console.error('Error checking registration:', error);
    return { success: false, isRegistered: false, error: error.message };
  }
}

/**
 * Anuluje rejestrację użytkownika
 */
export async function cancelRegistration(
  registrationId: string,
  userId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('training_registrations')
      .update({ registration_status: 'cancelled' })
      .eq('id', registrationId)
      .eq('user_id', userId);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error('Error cancelling registration:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Pobiera rejestracje użytkownika
 */
export async function getUserRegistrations(
  userId: string
): Promise<{ success: boolean; data?: TrainingRegistration[]; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('training_registrations')
      .select(`
        *,
        training:trainings (*)
      `)
      .eq('user_id', userId)
      .order('registration_date', { ascending: false });

    if (error) throw error;

    return { success: true, data: data || [] };
  } catch (error: any) {
    console.error('Error fetching user registrations:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Pobiera statystyki szkoleń
 */
export async function getTrainingsStats(): Promise<{
  success: boolean;
  data?: {
    totalTrainings: number;
    publishedTrainings: number;
    draftTrainings: number;
    totalRegistrations: number;
    upcomingTrainings: number;
  };
  error?: string;
}> {
  try {
    const [
      { data: trainings, error: trainingsError },
      { count: registrationsCount, error: registrationsError },
    ] = await Promise.all([
      supabase.from('trainings').select('id, status, start_date'),
      supabase
        .from('training_registrations')
        .select('*', { count: 'exact', head: true })
        .in('registration_status', ['registered', 'confirmed']),
    ]);

    if (trainingsError) throw trainingsError;
    if (registrationsError) throw registrationsError;

    const now = new Date();
    const publishedTrainings = trainings?.filter((t) => t.status === 'published').length ?? 0;
    const draftTrainings = trainings?.filter((t) => t.status === 'draft').length ?? 0;
    const upcomingTrainings =
      trainings?.filter(
        (t) => t.status === 'published' && t.start_date && new Date(t.start_date) > now
      ).length ?? 0;

    return {
      success: true,
      data: {
        totalTrainings: trainings?.length ?? 0,
        publishedTrainings,
        draftTrainings,
        totalRegistrations: registrationsCount ?? 0,
        upcomingTrainings,
      },
    };
  } catch (error: any) {
    console.error('Error fetching trainings stats:', error);
    return { success: false, error: error.message };
  }
}

